import type { Program } from "@/types";
import type { JourneyWithDays } from "@/hooks/useJourneys";
import type { CourseBlock, CourseDetails } from "@/utils/courseJourneys";
import type { WritingPreviewPrompt } from "@/components/journeys/course/WritingPreviewContext";

export const PREVIEW_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const lifetime = 4 * 60 * 60 * 1000;
interface SnapshotBase {
  version: 1;
  token: string;
  userId: string;
  createdAt: number;
  sourceId: string | null;
  from: "admin" | "creator";
}
export interface CourseAuthorSnapshot extends SnapshotBase {
  kind: "course";
  journey: JourneyWithDays;
}
export interface ProgramAuthorSnapshot extends SnapshotBase {
  kind: "program";
  program: Program;
  prompts: WritingPreviewPrompt[];
  editor: "details" | "prompts" | "admin";
  editorState?: unknown;
  journeyReturn?: string | null;
}
export type AuthorSnapshot = CourseAuthorSnapshot | ProgramAuthorSnapshot;
const key = (userId: string, token: string) =>
  `jp:author-preview:${userId}:${token}`;
const record = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));
const optionalText = (value: unknown, length = 200000) =>
  value == null || (typeof value === "string" && value.length <= length);
const image = (value: unknown) =>
  value == null ||
  value === "" ||
  (typeof value === "string" &&
    (value.startsWith("https://") ||
      /^data:image\/(png|jpeg|webp);base64,[a-z0-9+/=]+$/i.test(value)));

function validCourse(value: unknown): value is JourneyWithDays {
  if (
    !record(value) ||
    !PREVIEW_UUID.test(String(value.id)) ||
    !record(value.experience_config) ||
    value.experience_config.version !== "course_v1" ||
    !Array.isArray(value.days) ||
    value.days.length > 500 ||
    typeof value.title !== "string" ||
    !optionalText(value.description) ||
    !image(value.thumbnail_url)
  )
    return false;
  const ids = new Set<string>();
  return value.days.every((unit: unknown) => {
    if (
      !record(unit) ||
      typeof unit.id !== "string" ||
      !PREVIEW_UUID.test(unit.id) ||
      ids.has(unit.id) ||
      !Number.isInteger(unit.day_number) ||
      Number(unit.day_number) < 1 ||
      Number(unit.day_number) > 365 ||
      !Number.isInteger(unit.sort_order) ||
      !["text", "video", "powerprompt", "freestyle", "download"].includes(
        String(unit.unit_type),
      )
    )
      return false;
    ids.add(unit.id);
    return (
      [
        "title",
        "description",
        "content",
        "lesson_title",
        "audio_url",
        "video_url",
        "download_url",
        "featured_image_url",
        "featured_image_alt",
      ].every((field) => optionalText(unit[field])) &&
      (!unit.program_id || PREVIEW_UUID.test(String(unit.program_id))) &&
      (unit.duration_minutes == null ||
        (Number.isInteger(unit.duration_minutes) &&
          Number(unit.duration_minutes) >= 1 &&
          Number(unit.duration_minutes) <= 120))
    );
  });
}
function validProgram(value: unknown): value is Program {
  return (
    record(value) &&
    PREVIEW_UUID.test(String(value.id)) &&
    typeof value.title === "string" &&
    value.title.length <= 1000 &&
    optionalText(value.description) &&
    image(value.thumbnail_url) &&
    ["powerprompt", "powerPrompt"].includes(String(value.program_type))
  );
}
function validPrompts(value: unknown): value is WritingPreviewPrompt[] {
  return (
    Array.isArray(value) &&
    value.length <= 500 &&
    value.every(
      (prompt) =>
        record(prompt) &&
        typeof prompt.id === "string" &&
        typeof prompt.content === "string" &&
        prompt.content.length <= 200000 &&
        optionalText(prompt.title, 1000) &&
        Number.isInteger(prompt.duration) &&
        Number(prompt.duration) >= 1 &&
        Number(prompt.duration) <= 7200 &&
        Number.isInteger(prompt.order_index),
    )
  );
}
export function validateAuthorSnapshot(
  value: unknown,
  userId: string,
  token: string,
): value is AuthorSnapshot {
  if (
    !record(value) ||
    value.version !== 1 ||
    value.userId !== userId ||
    value.token !== token ||
    !PREVIEW_UUID.test(token) ||
    !Number.isFinite(value.createdAt) ||
    Number(value.createdAt) > Date.now() + 60000 ||
    Date.now() - Number(value.createdAt) > lifetime ||
    (value.sourceId !== null && !PREVIEW_UUID.test(String(value.sourceId))) ||
    !["creator", "admin"].includes(String(value.from))
  )
    return false;
  if (value.kind === "course")
    return (
      validCourse(value.journey) &&
      (!value.sourceId || value.journey.id === value.sourceId)
    );
  return (
    value.kind === "program" &&
    validProgram(value.program) &&
    validPrompts(value.prompts) &&
    ["details", "prompts", "admin"].includes(String(value.editor)) &&
    (!value.sourceId || value.program.id === value.sourceId)
  );
}
export function readAuthorSnapshot(
  userId: string | undefined,
  token: string | null,
): AuthorSnapshot | null {
  if (!userId || !token || !PREVIEW_UUID.test(token)) return null;
  try {
    const value: unknown = JSON.parse(
      sessionStorage.getItem(key(userId, token)) || "null",
    );
    return validateAuthorSnapshot(value, userId, token) ? value : null;
  } catch {
    return null;
  }
}
export function storeAuthorSnapshot(snapshot: AuthorSnapshot) {
  if (!validateAuthorSnapshot(snapshot, snapshot.userId, snapshot.token))
    throw new Error(
      "This preview could not be prepared. Check the activities and try again.",
    );
  sessionStorage.setItem(
    key(snapshot.userId, snapshot.token),
    JSON.stringify(snapshot),
  );
  return snapshot.token;
}
export function removeAuthorSnapshot(userId: string, token: string) {
  try {
    sessionStorage.removeItem(key(userId, token));
  } catch {
    /* Expiry still bounds inaccessible storage. */
  }
}
export function makeCourseSnapshot(input: {
  userId: string;
  source?: JourneyWithDays;
  details: CourseDetails;
  blocks: CourseBlock[];
  from: "admin" | "creator";
}): CourseAuthorSnapshot {
  const token = crypto.randomUUID();
  const id = input.source?.id || crypto.randomUUID();
  const now = new Date().toISOString();
  const { details } = input;
  const journey: JourneyWithDays = {
    slug: null,
    hero_thumbnail_url: null,
    price_cents: null,
    daily_reminder_enabled: false,
    daily_reminder_send_hour_local: 9,
    is_featured: false,
    tags: [],
    sort_order: 0,
    created_at: now,
    ...input.source,
    ...details,
    id,
    title: details.title || "Untitled Journey",
    created_by: input.userId,
    description: details.description,
    thumbnail_url: details.thumbnail_url || null,
    duration_days: new Set(input.blocks.map((block) => block.day_number)).size,
    updated_at: token,
    is_published: false,
    experience_config: {
      ...(record(input.source?.experience_config)
        ? input.source.experience_config
        : {}),
      version: "course_v1",
      completion_image_url: details.completion_image_url || null,
      schedule: { mode: details.schedule_mode || "self_paced", timezone: details.schedule_timezone || "UTC", release_hour: details.schedule_release_hour ?? 9, start_date: details.schedule_start_date || null },
    },
    days: input.blocks.map(({ program: _program, ...block }) => ({
      ...block,
      journey_id: id,
    })),
  };
  return {
    version: 1,
    token,
    userId: input.userId,
    createdAt: Date.now(),
    sourceId: input.source?.id || null,
    from: input.from,
    kind: "course",
    journey,
  };
}
export function programPreviewPath(
  programId: string,
  from: "creator" | "admin",
  token?: string,
  list = false,
) {
  const params = new URLSearchParams({
    preview: "1",
    previewProgram: "1",
    previewFrom: from,
  });
  if (token) params.set("previewSnapshot", token);
  if (list) params.set("previewList", "1");
  return `/session/${programId}?${params}`;
}
export function normalizePreviewProgram(
  input: Partial<Program> | Record<string, unknown>,
  id: string,
): Program {
  return {
    id,
    title:
      typeof input.title === "string" && input.title
        ? input.title
        : "Untitled PowerPrompt",
    description: typeof input.description === "string" ? input.description : "",
    thumbnail_url:
      typeof input.thumbnail_url === "string" ? input.thumbnail_url : null,
    access_tier: ["free", "members", "premium", "specialty"].includes(
      String(input.access_tier),
    )
      ? (input.access_tier as Program["access_tier"])
      : "free",
    program_type: "powerprompt",
    prompt_count: Number(input.prompt_count) || 0,
    total_duration: Number(input.total_duration) || 0,
    is_published: input.is_published === true,
    created_by: typeof input.created_by === "string" ? input.created_by : null,
    creation_source: input.creation_source === "admin" ? "admin" : "frontend",
    host_mascot_id:
      typeof input.host_mascot_id === "string" ? input.host_mascot_id : null,
    mental_health_approved: input.mental_health_approved === true,
    tags: Array.isArray(input.tags) ? input.tags : [],
  };
}
